import { isLoading } from "apollo/utils";
import {
  BACKEND,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { tokenAccessor } from "core/model/api/transport";
import { getBackendConfig } from "core/model/config";
import { formatISODate } from "core/model/utils/dates";
import { QueryProgress } from "core/types";
import { Banner, Spinner } from "ds/ui";
import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "translations";

type NumericVitalValue = { type: "NM"; number: number };
type StringVitalValue = { type: "ST"; string: string };

type PatientNote = {
  title: string;
  text: string;
  domain?: string | null;
  authored_at: string;
  idempotency_key: string;
  tags: string[];
};

type PatientVital = {
  value: NumericVitalValue | StringVitalValue;
  code: { system: string; value: string; text: string };
  unit: { system: string; code: string; text: string };
  note?: string | null;
  observed_at: string;
  idempotency_key: string;
  tags: string[];
};

type PatientData = {
  notes: PatientNote[];
  vitals: PatientVital[];
};

function isPatientData(data: unknown): data is PatientData {
  return (
    typeof data === "object" &&
    data !== null &&
    ("notes" in data || "vitals" in data)
  );
}

function formatVitalValue(vital: PatientVital): string {
  if (vital.value.type === "NM") {
    return `${vital.value.number} ${vital.unit.text}`;
  }
  return vital.value.string;
}

function GatewayPatientDataViewer({
  data,
}: {
  data: PatientData;
}) {
  const translations = useTranslations();
  const locale = useLocale();
  const notes = [...data.notes].sort(
    (a, b) =>
      new Date(b.authored_at).getTime() - new Date(a.authored_at).getTime(),
  );
  const vitals = [...data.vitals].sort(
    (a, b) =>
      new Date(b.observed_at).getTime() - new Date(a.observed_at).getTime(),
  );

  return (
    <div className="flex flex-col overflow-y-auto px-9 py-[30px]">
      {notes.length > 0 && (
        <div className="flex flex-col gap-[18px]">
          {notes.map((note, i) => (
            <div key={note.idempotency_key} className="flex flex-col gap-[18px]">
              {i > 0 && <hr className="border-gray-400" />}
              <div className="flex flex-col gap-1">
                <p className="text-heading-xl font-bold">{note.title}</p>
                <p className="text-body-md text-gray-600">
                  {translations.extractDocs.splitView.sourcesTab.kisDataNoteLastUpdated(
                    { date: formatISODate(note.authored_at, locale) },
                  )}
                </p>
              </div>
              <hr className="border-gray-400" />
              <p className="text-body-sm whitespace-pre-wrap">{note.text}</p>
            </div>
          ))}
        </div>
      )}

      {vitals.length > 0 && (
        <div className="flex flex-col gap-3 mt-6">
          {notes.length > 0 && <hr className="border-gray-400" />}
          <p className="text-heading-xl font-bold">
            {translations.extractDocs.splitView.sourcesTab.kisDataVitalsHeading}
          </p>
          <hr className="border-gray-400" />
          <div className="flex flex-col divide-y divide-gray-200">
            {vitals.map((vital) => (
              <div
                key={vital.idempotency_key}
                className="flex items-center justify-between gap-4 py-2"
              >
                <span className="text-body-md flex-1">{vital.code.text}</span>
                <span className="text-body-md font-bold shrink-0">
                  {formatVitalValue(vital)}
                </span>
                <span className="text-body-sm text-gray-600 shrink-0">
                  {formatISODate(vital.observed_at, locale)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function KisJsonViewer({ url }: { url: string }) {
  const translations = useTranslations();
  const aiCoreConfig = getBackendConfig(BACKEND.AI_CORE);
  const token = tokenAccessor();
  const [patientData, setPatientData] = useState<PatientData | null>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  useEffect(() => {
    if (!url) {
      setPatientData(null);
      setRawText(null);
      setProgress(QUERY_PROGRESS_NOT_STARTED);
      return;
    }

    let cancelled = false;
    setProgress(QUERY_PROGRESS_PENDING);
    setPatientData(null);
    setRawText(null);

    const isAiCoreHostedFile =
      !!aiCoreConfig?.host && url.includes(aiCoreConfig.host);
    const headers: HeadersInit = {};
    if (isAiCoreHostedFile && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    fetch(url, { headers })
      .then(async (res) => {
        if (!res.ok) {
          console.error(
            "No fetch response in KisJsonViewer:",
            `${res.status} ${res.statusText}`,
          );
          setProgress(QUERY_PROGRESS_FAILED);
        }
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        try {
          const parsed = JSON.parse(text);
          if (isPatientData(parsed)) {
            setPatientData(parsed);
          } else {
            setRawText(JSON.stringify(parsed, null, 2));
          }
        } catch {
          setRawText(text);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProgress(QUERY_PROGRESS_FAILED);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setProgress(QUERY_PROGRESS_SUCCEED);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [url, aiCoreConfig?.host, token, translations]);

  if (progress === QUERY_PROGRESS_FAILED) {
    return (
      <Banner
        color="danger"
        message={translations.extractDocs.splitView.sourcesTab.errorNoHisData}
      />
    );
  }

  if (isLoading(progress)) {
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <Spinner color="primary" size="lg" testId="spinner-loading-kis-json" />
      </div>
    );
  }

  if (patientData) {
    return <GatewayPatientDataViewer data={patientData} />;
  }

  return (
    <pre className="max-h-[70vh] overflow-auto rounded-lg border border-gray-400 bg-gray-50 p-4 text-left font-mono text-body-sm break-all whitespace-pre-wrap">
      {rawText ?? ""}
    </pre>
  );
}
